import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ROMANIAN_COUNTIES } from "@/lib/romanianCounties";
import { validateCuiCnp } from "@/lib/cuiValidation";
import { cn } from "@/lib/utils";

interface SellerFormProps {
  sellerName: string;
  sellerCui: string;
  sellerAddress: string;
  sellerCity: string;
  sellerCounty: string;
  onSellerNameChange: (v: string) => void;
  onSellerCuiChange: (v: string) => void;
  onSellerAddressChange: (v: string) => void;
  onSellerCityChange: (v: string) => void;
  onSellerCountyChange: (v: string) => void;
  /** Field IDs flagged invalid by validation (e.g. "seller_cui"). */
  invalidFields?: Set<string>;
}

const errorClass = "border-destructive focus-visible:ring-destructive";

const SellerForm = ({
  sellerName, sellerCui, sellerAddress, sellerCity, sellerCounty,
  onSellerNameChange, onSellerCuiChange, onSellerAddressChange, onSellerCityChange, onSellerCountyChange,
  invalidFields,
}: SellerFormProps) => {
  const cuiValidation = validateCuiCnp(sellerCui);
  const isInvalid = (field: string) => invalidFields?.has(field) ?? false;

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4">
      <h3 className="font-heading font-semibold">Furnizor</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Denumire</Label>
          <Input
            data-invalid-field="seller_name"
            aria-invalid={isInvalid("seller_name")}
            value={sellerName}
            onChange={(e) => onSellerNameChange(e.target.value)}
            placeholder="Numele firmei"
            autoComplete="organization"
            className={cn(isInvalid("seller_name") && errorClass)}
          />
        </div>
        <div className="space-y-2">
          <Label>CUI / CIF / CNP</Label>
          <Input
            data-invalid-field="seller_cui"
            aria-invalid={!cuiValidation.valid || isInvalid("seller_cui")}
            value={sellerCui}
            onChange={(e) => onSellerCuiChange(e.target.value)}
            placeholder="ex: RO12345678, CIF sau CNP"
            className={cn((!cuiValidation.valid || isInvalid("seller_cui")) && errorClass)}
          />
          {!cuiValidation.valid && cuiValidation.error && (
            <p className="text-xs text-destructive">{cuiValidation.error}</p>
          )}
          {cuiValidation.valid && cuiValidation.type === "cnp" && sellerCui.trim() && (
            <p className="text-xs text-muted-foreground">Detectat: CNP (persoană fizică)</p>
          )}
          {cuiValidation.valid && cuiValidation.type === "cui" && sellerCui.trim() && (
            <p className="text-xs text-muted-foreground">Detectat: CUI (firmă)</p>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Label>Adresă (stradă)</Label>
        <Input
          data-invalid-field="seller_address"
          aria-invalid={isInvalid("seller_address")}
          value={sellerAddress}
          onChange={(e) => onSellerAddressChange(e.target.value)}
          placeholder="Str. Exemplu nr. 1"
          autoComplete="street-address"
          className={cn(isInvalid("seller_address") && errorClass)}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Oraș</Label>
          <Input
            data-invalid-field="seller_city"
            aria-invalid={isInvalid("seller_city")}
            value={sellerCity}
            onChange={(e) => onSellerCityChange(e.target.value)}
            placeholder="ex: București"
            autoComplete="address-level2"
            className={cn(isInvalid("seller_city") && errorClass)}
          />
        </div>
        <div className="space-y-2" data-field-group="seller_county">
          <Label>Județ</Label>
          <Select value={sellerCounty} onValueChange={onSellerCountyChange}>
            <SelectTrigger
              data-invalid-field="seller_county"
              aria-invalid={isInvalid("seller_county")}
              className={cn(isInvalid("seller_county") && errorClass)}
            >
              <SelectValue placeholder="Selectează județul" />
            </SelectTrigger>
            <SelectContent>
              {ROMANIAN_COUNTIES.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.name} ({c.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default SellerForm;
